import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { formatDate } from '@/utils/format';

import {
  AssetDialog,
  type AssetFormValues,
  type AssetRow,
} from './asset-dialog';
import { AssetsSubnav } from './assets-subnav';

export const AssetsPage = () => {
  const { t } = useTranslation('assets');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canCreate = hasPermission(membership, PERMISSIONS.ASSETS_CREATE);
  const canEdit = hasPermission(membership, PERMISSIONS.ASSETS_EDIT);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AssetRow | null>(null);

  const list = useQuery({
    queryKey: ['assets', page, pageSize],
    queryFn: () =>
      api.get<{ data: AssetRow[]; page: OffsetPage }>('/assets', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: (values: AssetFormValues) => {
      const body = {
        name: values.name,
        kind: values.kind,
        condition: values.condition,
        acquiredOn: values.acquiredOn,
        notes: values.notes || null,
        registrationNumber:
          values.kind === 'vehicle' ? values.registrationNumber || null : null,
      };
      if (editing) {
        return api.patch<AssetRow>(`/assets/${editing.id}`, body);
      }
      return api.post<AssetRow>('/assets', body);
    },
    onSuccess: async () => {
      toast.success(t('saved'));
      setDialogOpen(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const columns: DataTableColumn<AssetRow>[] = [
    {
      id: 'acquiredOn',
      header: t('columns.acquiredOn'),
      cell: (row) => (row.acquiredOn ? formatDate(row.acquiredOn) : '—'),
    },
    {
      id: 'name',
      header: t('columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'kind',
      header: t('columns.kind'),
      cell: (row) => t(`kind.${row.kind}`),
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => <Badge variant="neutral">{t(`status.${row.status}`)}</Badge>,
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        headerActions={
          canCreate ? (
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              {t('create')}
            </Button>
          ) : null
        }
      />

      <AssetsSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 py-4 text-center">
            <p className="text-body font-medium">{t('empty.title')}</p>
            <p className="text-body-sm text-fg-secondary">{t('empty.description')}</p>
            {canCreate ? (
              <div className="flex justify-end sm:justify-center">
                <Button
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  {t('empty.action')}
                </Button>
              </div>
            ) : null}
          </div>
        }
        onRowClick={
          canEdit
            ? (row) => {
                setEditing(row);
                setDialogOpen(true);
              }
            : undefined
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <AssetDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        asset={editing}
        submitting={save.isPending}
        onSubmit={(values) => save.mutate(values)}
      />
    </div>
  );
};
